/**
 * Theme money formatting for client-only price updates.
 *
 * Liquid-rendered Shopify money strings remain the primary source of truth.
 * This module is only for live UI states where Shopify returns numeric cents
 * without section HTML. It reads the same shop money formats and Fitcheck
 * currency-code display settings published by Liquid.
 */

const DEFAULT_MONEY_FORMAT = '{{ amount }}';
const DEFAULT_MONEY_WITH_CURRENCY_FORMAT = '{{ amount }} {{ currency }}';

function resolveFitCheckGlobal() {
  const fitCheckGlobal = window.FitCheck ?? (typeof FitCheck !== 'undefined' ? FitCheck : null);

  if (fitCheckGlobal && window.FitCheck !== fitCheckGlobal) {
    window.FitCheck = fitCheckGlobal;
  }

  return fitCheckGlobal;
}

function normalizeSettings(settings = null) {
  const source = settings || resolveFitCheckGlobal()?.money || {};

  return {
    currencyCodeDisplay: source.currencyCodeDisplay || source.currency_code_display || 'all',
    currencyCodeEnabledProductPages:
      source.currencyCodeEnabledProductPages ?? source.currency_code_enabled_product_pages ?? true,
    currencyCodeEnabledProductItems:
      source.currencyCodeEnabledProductItems ?? source.currency_code_enabled_product_items ?? true,
    currencyCodeEnabledCartItems:
      source.currencyCodeEnabledCartItems ?? source.currency_code_enabled_cart_items ?? true,
    currencyCodeEnabledCartTotal:
      source.currencyCodeEnabledCartTotal ?? source.currency_code_enabled_cart_total ?? true,
    moneyFormat: source.moneyFormat || source.money_format || DEFAULT_MONEY_FORMAT,
    moneyWithCurrencyFormat:
      source.moneyWithCurrencyFormat || source.money_with_currency_format || DEFAULT_MONEY_WITH_CURRENCY_FORMAT,
    currencyCode: source.currencyCode || source.currency_code || window.Shopify?.currency?.active || '',
  };
}

function scopeSetting(settings, scope) {
  switch (scope) {
    case 'productPages':
      return settings.currencyCodeEnabledProductPages;
    case 'productItems':
      return settings.currencyCodeEnabledProductItems;
    case 'cartItems':
      return settings.currencyCodeEnabledCartItems;
    case 'cartTotal':
      return settings.currencyCodeEnabledCartTotal;
    default:
      return false;
  }
}

export function shouldUseCurrencyCode(scope = 'productItems', settings = null) {
  const fitSettings = normalizeSettings(settings);

  if (fitSettings.currencyCodeDisplay === 'all') return true;
  if (fitSettings.currencyCodeDisplay === 'custom') return Boolean(scopeSetting(fitSettings, scope));
  return false;
}

function toCents(value) {
  const parsed = Number.parseInt(String(value ?? ''), 10);
  return Number.isFinite(parsed) ? parsed : null;
}

function groupThousands(value, separator) {
  if (!separator) return value;

  return value.replace(/\B(?=(\d{3})+(?!\d))/g, separator);
}

function formatAmount(cents, decimals, decimalSeparator, thousandsSeparator) {
  const negative = cents < 0;
  const absolute = Math.abs(cents);
  const value = (absolute / 100).toFixed(decimals);
  const parts = value.split('.');
  const whole = groupThousands(parts[0], thousandsSeparator);
  const fraction = decimals > 0 ? decimalSeparator + parts[1] : '';

  return (negative ? '-' : '') + whole + fraction;
}

function replacementForPlaceholder(name, cents, currencyCode) {
  switch (name) {
    case 'amount':
      return formatAmount(cents, 2, '.', ',');
    case 'amount_no_decimals':
      return formatAmount(cents, 0, '.', ',');
    case 'amount_with_comma_separator':
      return formatAmount(cents, 2, ',', '.');
    case 'amount_no_decimals_with_comma_separator':
      return formatAmount(cents, 0, ',', '.');
    case 'amount_with_apostrophe_separator':
      return formatAmount(cents, 2, '.', "'");
    case 'amount_no_decimals_with_space_separator':
      return formatAmount(cents, 0, '.', ' ');
    case 'amount_with_space_separator':
      return formatAmount(cents, 2, '.', ' ');
    case 'amount_with_period_and_space_separator':
      return formatAmount(cents, 2, '.', ' ');
    case 'currency':
      return currencyCode || '';
    default:
      return '';
  }
}

function stripHTML(value) {
  const text = String(value ?? '');
  if (!text.includes('<')) return text;

  const template = document.createElement('template');
  template.innerHTML = text;
  return template.content.textContent || '';
}

export function formatFitMoney(cents, options = {}) {
  const amount = toCents(cents);
  if (amount === null) return '';

  const fitSettings = normalizeSettings(options.moneySettings);
  const useCurrency = options.useCurrency ?? shouldUseCurrencyCode(options.scope, fitSettings);
  const format = useCurrency ? fitSettings.moneyWithCurrencyFormat : fitSettings.moneyFormat;
  const source = String(format || DEFAULT_MONEY_FORMAT);
  const template = source.includes('{{') ? source : DEFAULT_MONEY_FORMAT;
  const formatted = template.replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (_, name) =>
    replacementForPlaceholder(name, amount, fitSettings.currencyCode),
  );

  return stripHTML(formatted).trim();
}
